// src/contexts/AuthContext.js - Modified to log redirects
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { api } from '../services/api.js';
import { loginUser, registerUser, getCurrentUser, updateUserProfile } from '../services/api.js';
import logger from '../utils/logger.js';
import useErrorHandler from '../hooks/useErrorHandler.js';

// Create the auth context
const AuthContext = createContext({
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  login: () => {},
  register: () => {},
  logout: () => {},
  updateProfile: () => {},
  refreshUser: () => {}
});

// Custom hook for using the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();

  // Check for token and load user on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const userData = await getCurrentUser();
        setUser(userData);
      } catch (err) {
        handleError(err, 'Loading user');
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      const { token, user: userData } = await loginUser(email, password);
      localStorage.setItem('token', token);
      
      // Refresh user data to get the most up-to-date subscription information
      // This ensures we have the latest data after the server processes the subscription
      const refreshedUserData = await getCurrentUser();
      setUser(refreshedUserData);
      
      return refreshedUserData;
    } catch (err) {
      handleError(err, 'Login');
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);
      // Always forward consent flags. registerUser rebuilds the JSON body, so
      // omitting these keys is what made production POST without them.
      const isAccepted = (value) =>
        value === true || value === 'true' || value === 1 || value === '1';
      const acceptTerms = isAccepted(userData.acceptTerms);
      const acceptedTerms = acceptTerms || isAccepted(userData.acceptedTerms);
      const acceptedPrivacy = acceptTerms || isAccepted(userData.acceptedPrivacy);

      const { token, user: newUser } = await registerUser({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        password: userData.password,
        acceptTerms,
        acceptedTerms,
        acceptedPrivacy
      });
      localStorage.setItem('token', token);
      setUser(newUser);
      return newUser;
    } catch (err) {
      handleError(err, 'Registration');
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  }, [navigate]);

  // Update profile function
  const updateProfile = useCallback(async (updates) => {
    try {
      setLoading(true);
      setError(null);
      const response = await updateUserProfile(updates);
      
      if (!response || !response.user) {
        throw new Error('Profile update failed: Invalid response');
      }
      
      // Update user state with new data
      const updatedUser = {
        ...user,
        ...response.user,
        profile: {
          ...user.profile,
          ...response.user.profile
        }
      };
      
      // Update user state
      setUser(updatedUser);
      
      // Update localStorage
      const currentUser = {
        ...updatedUser,
        token: localStorage.getItem('token')
      };
      localStorage.setItem('user', JSON.stringify(currentUser));
      
      // Return the updated user data
      return response;
    } catch (err) {
      logger.error('Profile update error:', err);
      setError(err.message || 'Profile update failed');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await getCurrentUser();
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message || 'Failed to refresh user data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    refreshUser
  }), [user, loading, error, login, register, logout, updateProfile, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;